import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, Shield } from 'lucide-react';
import { environment } from '../config/environment';
import { signInWithProjectToken } from '../lib/projectAuth';
import {
  getCustomerTokenVerificationError,
  shouldPromptCustomerTokenContinue,
} from '../utils/customerMagicLink';

export function AuthVerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'ready' | 'verifying' | 'success' | 'error'>('ready');
  const [errorMessage, setErrorMessage] = useState('');
  const [tokenConsumed, setTokenConsumed] = useState(false);

  const token = searchParams.get('token');
  const showContinueGate = shouldPromptCustomerTokenContinue(token, tokenConsumed) && status === 'ready';

  const handleContinueWithToken = async () => {
    if (!token || tokenConsumed) {
      setStatus('error');
      setErrorMessage('No verification token provided');
      return;
    }

    setTokenConsumed(true);
    setStatus('verifying');

    try {
      const functionsUrl = environment.useEmulators
        ? `http://127.0.0.1:5001/ima-onboard/us-central1/verifyMagicLink`
        : `https://us-central1-ima-onboard.cloudfunctions.net/verifyMagicLink`;
      const response = await fetch(functionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const result = await response.json();
      const verificationError = getCustomerTokenVerificationError(response.ok, result);

      if (!verificationError) {
        if (!result.customToken) {
          setStatus('error');
          setErrorMessage('Authentication token missing from server response');
          return;
        }

        await signInWithProjectToken(result.customToken);
        setStatus('success');
        setTimeout(() => {
          navigate(`/project/${result.jiraKey}`);
        }, 2000);
      } else {
        setStatus('error');
        setErrorMessage(verificationError);
      }
    } catch (error) {
      console.error('Error verifying magic link:', error);
      setStatus('error');
      setErrorMessage('Failed to verify magic link. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex flex-col items-center space-y-4">
            <img
              src="/ima_onboard_logo.png"
              alt="Ideal Mobile App Logo"
              className="h-20 w-20 object-contain"
            />
            <div className="flex flex-col items-center space-y-1">
              <h1 className="text-4xl font-bold text-gray-900">Ideal Mobile App</h1>
              <span className="text-xl text-gray-600 font-medium">Onboarding Portal</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200 text-center">
          {showContinueGate && (
            <>
              <Shield className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Continue to Your Portal</h2>
              <p className="text-gray-600 mb-6">
                Click below to verify your access link. This helps prevent email security scanners
                from using the link before you do.
              </p>
              <button
                type="button"
                onClick={handleContinueWithToken}
                className="w-full bg-blue-600 text-white px-4 py-2.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Continue to Portal
              </button>
            </>
          )}

          {status === 'verifying' && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Verifying Your Access</h2>
              <p className="text-gray-600">Please wait while we verify your magic link...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Verified!</h2>
              <p className="text-gray-600">Redirecting you to your project...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Verification Failed</h2>
              <p className="text-gray-600 mb-4">{errorMessage}</p>
              <button
                onClick={() => navigate('/auth')}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Try Again
              </button>
            </>
          )}

          {status === 'ready' && !token && (
            <>
              <XCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Verification Failed</h2>
              <p className="text-gray-600 mb-4">No verification token provided</p>
              <button
                onClick={() => navigate('/auth')}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Try Again
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
